import { PostalAddress, type Invoice, type InvoiceItem } from "../../lib/gboModels";
import { selectNoPaymentDue } from "../checkout/checkoutSelectors";

export function getInvoiceViewModel(invoice: Invoice) {
  const noPaymentDue = selectNoPaymentDue(invoice.balance, invoice.closed);
  const serviceLocation = getDistinctServiceLocation(invoice);

  return {
    document: {
      id: invoice.id,
      voided: invoice.voided,
      code: invoice.code,
      billToName: invoice.billToName,
      billToAddress: invoice.billToAddress.formattedAddress,
      serviceLocation: serviceLocation?.formattedAddress ?? null,
      serviceLocationName:
        serviceLocation == null ? null : invoice.serviceLocationName?.trim() || null,
      issuedOn: invoice.issuedOn,
      dueBy: invoice.dueBy,
    },
    statement: {
      items: getInvoiceStatementItems(invoice.items),
      totals: [
        { label: "Subtotal", amount: invoice.subtotal },
        { label: "Tax", amount: invoice.tax },
        { label: "Total", amount: invoice.total },
        { label: "Paid", amount: invoice.paid },
        { label: "Balance", amount: invoice.balance, emphasis: true },
      ],
    },
    paymentRail: {
      balanceLabel: invoice.voided ? "Void invoice" : noPaymentDue ? "Status" : "Balance due",
      balanceValue: invoice.voided ? 0 : invoice.balance,
      balanceValueKind: !invoice.voided && noPaymentDue ? ("paid" as const) : ("money" as const),
    },
    noPaymentDue,
  };
}

function getDistinctServiceLocation(
  invoice: Pick<Invoice, "billToAddress" | "serviceLocation" | "serviceLocationName">,
): Invoice["serviceLocation"] {
  if (
    invoice.serviceLocation == null ||
    (PostalAddress.isSame(invoice.serviceLocation, invoice.billToAddress) &&
      !invoice.serviceLocationName?.trim())
  )
    return null;

  return invoice.serviceLocation;
}

export function getInvoiceStatementItems(items: InvoiceItem[]) {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const typeComparison =
        getStatementItemSortGroup(left.item) - getStatementItemSortGroup(right.item);
      if (typeComparison !== 0) return typeComparison;

      const dateComparison = Date.parse(left.item.created) - Date.parse(right.item.created);
      if (dateComparison !== 0 && !Number.isNaN(dateComparison)) return dateComparison;

      return left.index - right.index;
    })
    .map(({ item }) => item);
}

function getStatementItemSortGroup(item: InvoiceItem) {
  return item.type === "Payment" || item.type === "AccountCredit" ? 1 : 0;
}
